import React, { useEffect, useRef, FC, useState } from 'react';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { MapContainer } from './InteractiveMap.styles';
import { MapComponentProps, Area } from './InteractiveMap.types';
import { Box, Typography, Alert, AlertTitle } from '@mui/material';
import { styled } from '@mui/material/styles';

// Helper function to determine area color based on mobile app logic
const getAreaColor = (area: Area): string => {
  // If area has a colorCode, use it
  if (area.colorCode) {
    return area.colorCode;
  }

  // Fallback logic matching mobile app
  const usersLength = area.users?.length || 0;
  const circlesLength = area.circles?.length || 0;
  const totalAssignees = usersLength + circlesLength;

  if (totalAssignees === 0) {
    return '#D0D2D8'; // Gray for no assignees
  } else if (totalAssignees === 1) {
    // Use the first assignee's color
    if (usersLength > 0) {
      const userColor =
        area.users?.[0]?.colorcode || area.users?.[0]?.colorCode;
      if (userColor) {
        return userColor;
      }
    } else if (circlesLength > 0) {
      const circleColor =
        area.circles?.[0]?.colorcode || area.circles?.[0]?.colorCode;
      if (circleColor) {
        return circleColor;
      }
    }
  } else if (totalAssignees > 1) {
    return '#5F27CD'; // for multiple assignees
  }

  // Default fallback
  console.log('Using default purple fallback');
  return '#5F27CD';
};

const MapFallbackContainer = styled(Box)(({ theme }) => ({
  width: '100%',
  height: '400px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: theme.custom?.background2 || '#f5f5f5',
  border: '1px solid #e0e0e0',
  borderRadius: '8px',
}));

const MapFallback: FC<{ reason?: string }> = ({
  reason = 'Map configuration unavailable',
}) => (
  <MapFallbackContainer>
    <Alert severity="warning" sx={{ maxWidth: 400 }}>
      <AlertTitle>Map Unavailable</AlertTitle>
      <Typography variant="body2">{reason}</Typography>
    </Alert>
  </MapFallbackContainer>
);

const InteractiveMapView: FC<MapComponentProps> = ({
  initialViewState,
  drawingEnabled = false,
  areas = [],
  selectedAreaId,
  userLocation,
  onDrawingClick,
  drawingCoords = [],
  onAreaClick,
  isEditMode = false,
  editingAreaId,
  onCoordinateAdd,
  onCoordinateMove,
  onCoordinateDelete,
  mapboxToken,
  onMapReady,
}) => {
  const mapContainer = useRef<HTMLDivElement>(null);
  const map = useRef<any>(null);
  const coordinateMarkers = useRef<any[]>([]);
  const drawingMarkers = useRef<any[]>([]);
  const mapInitialized = useRef<boolean>(false);
  const userLocationRef = useRef(userLocation);
  const [coordinateUpdateTrigger, setCoordinateUpdateTrigger] = useState(0);
  const [mapError, setMapError] = useState<string | null>(null);

  // Keep userLocationRef in sync with userLocation prop
  useEffect(() => {
    userLocationRef.current = userLocation;
  }, [userLocation]);

  // Early return if no Mapbox token is configured
  if (!mapboxToken) {
    return (
      <MapFallback reason="Mapbox API key is not configured for this organization. Please contact your administrator." />
    );
  }

  // Early return if there's a map error
  if (mapError) {
    return <MapFallback reason={mapError} />;
  }

  useEffect(() => {
    // Prevent double initialization (React Strict Mode can cause this)
    if (mapInitialized.current || map.current) {
      return;
    }

    if (!mapContainer.current) {
      return;
    }

    mapInitialized.current = true;

    // Initialize map
    (mapboxgl as any).accessToken = mapboxToken;

    try {
      map.current = new mapboxgl.Map({
        container: mapContainer.current,
        style: 'mapbox://styles/mapbox/streets-v12',
        center: [initialViewState.longitude, initialViewState.latitude],
        zoom: initialViewState.zoom,
      });

      // Add navigation controls
      if (map.current) {
        map.current.addControl(new (mapboxgl as any).NavigationControl());
      }

      map.current.on('load', () => {
        // Add areas source and layers
        map.current.addSource('areas', {
          type: 'geojson',
          data: {
            type: 'FeatureCollection',
            features: [],
          },
        });

        // Add area fill layer
        map.current.addLayer({
          id: 'areas-fill',
          type: 'fill',
          source: 'areas',
          paint: {
            'fill-color': [
              'case',
              ['boolean', ['feature-state', 'selected'], false],
              ['get', 'colorCode'], // Selected color - use area's colorCode
              ['get', 'colorCode'], // Default color - use area's colorCode
            ],
            'fill-opacity': [
              'case',
              ['boolean', ['feature-state', 'selected'], false],
              0.6, // Selected opacity (increased for visibility)
              0.4, // Default opacity (increased for visibility)
            ],
          },
        });

        // Add area outline layer
        map.current.addLayer({
          id: 'areas-outline',
          type: 'line',
          source: 'areas',
          paint: {
            'line-color': [
              'case',
              ['boolean', ['feature-state', 'selected'], false],
              ['get', 'colorCode'], // Selected color - use area's colorCode
              ['get', 'colorCode'], // Default color - use area's colorCode
            ],
            'line-width': [
              'case',
              ['boolean', ['feature-state', 'selected'], false],
              4, // Selected width (increased for visibility)
              3, // Default width (increased for visibility)
            ],
          },
        });

        // Add user location source and layers
        map.current.addSource('user-location', {
          type: 'geojson',
          data: {
            type: 'FeatureCollection',
            features: [],
          },
        });

        // Add user location main dot layer with shadow
        map.current.addLayer({
          id: 'user-location-dot',
          type: 'circle',
          source: 'user-location',
          paint: {
            'circle-radius': [
              'interpolate',
              ['linear'],
              ['zoom'],
              10,
              6,
              20,
              9,
            ],
            'circle-color': '#4285F4',
            'circle-opacity': 1,
            'circle-stroke-width': 2,
            'circle-stroke-color': 'rgba(66, 133, 244, 0.3)',
            'circle-stroke-opacity': 1,
          },
        });

        // Start pulse animation by changing the radius
        let pulseDirection = 1;
        const baseRadius = { 10: 6, 20: 9 };
        let pulseScale = 1.0;
        const animatePulse = () => {
          if (map.current && map.current.getLayer('user-location-dot')) {
            pulseScale += pulseDirection * 0.003;
            if (pulseScale >= 1.15) {
              pulseDirection = -1;
            } else if (pulseScale <= 1.0) {
              pulseDirection = 1;
            }

            try {
              map.current.setPaintProperty(
                'user-location-dot',
                'circle-radius',
                [
                  'interpolate',
                  ['linear'],
                  ['zoom'],
                  10,
                  baseRadius[10] * pulseScale,
                  20,
                  baseRadius[20] * pulseScale,
                ]
              );
            } catch (error) {
              // Layer might not exist anymore, stop animation
              return;
            }
          }
          requestAnimationFrame(animatePulse);
        };

        // Start the animation after a short delay to ensure layer is ready
        setTimeout(() => {
          animatePulse();
        }, 100);

        // Add popup on hover
        const popup = new (mapboxgl as any).Popup({
          closeButton: false,
          closeOnClick: false,
        });

        map.current.on('mouseenter', 'areas-fill', (e: any) => {
          // Always check current edit mode state from a ref or state, not closure
          map.current.getCanvas().style.cursor = 'pointer';

          const feature = e.features[0];
          if (feature && feature.properties) {
            const coordinates = e.lngLat;
            const name = feature.properties.name || 'Unnamed Area';
            const description = feature.properties.description || '';
            const colorCode = feature.properties.colorCode || '#5F27CD';

            popup
              .setLngLat(coordinates)
              .setHTML(
                `
                <div style="padding: 8px; font-family: Arial, sans-serif;">
                  <strong style="color: ${colorCode};">${name}</strong>
                  ${
                    description
                      ? `<br><span style="color: #666; font-size: 12px;">${description}</span>`
                      : ''
                  }
                </div>
              `
              )
              .addTo(map.current);
          }
        });

        map.current.on('mouseleave', 'areas-fill', () => {
          map.current.getCanvas().style.cursor = '';
          popup.remove();
        });

        // Expose flyTo function to parent component
        if (onMapReady) {
          const flyToFunction = (
            longitude: number,
            latitude: number,
            zoomLevel: number = 15
          ) => {
            if (map.current) {
              map.current.flyTo({
                center: [longitude, latitude],
                zoom: zoomLevel,
                essential: true,
                duration: 2000,
              });
            }
          };
          onMapReady(flyToFunction);
        }
      });

      map.current.on('error', (e: any) => {
        console.error('Map error:', e);
        setMapError(
          'Map failed to load. Please check your internet connection and try again.'
        );
      });
    } catch (error) {
      console.error('Error creating map:', error);
      setMapError('Failed to initialize map. Please try refreshing the page.');
    }

    return () => {
      if (map.current) {
        map.current.remove();
        map.current = null;
      }
      mapInitialized.current = false;
    };
  }, [mapboxToken]);

  // Add click handlers separately to avoid map re-initialization
  useEffect(() => {
    if (!map.current) return;

    const handleMapClick = (e: any) => {
      // Check if click was on an area first
      const features = map.current.queryRenderedFeatures(e.point, {
        layers: ['areas-fill'],
      });

      if (features.length > 0) {
        // Area was clicked
        if (onAreaClick) {
          const feature = features[0];
          if (feature && feature.properties) {
            const areaId = feature.properties.id;
            const area = areas.find((a) => a.id === areaId);
            if (area) {
              onAreaClick(area);
            }
          }
        }
      } else {
        // Empty space was clicked
        if (drawingEnabled && onDrawingClick) {
          // In drawing mode, add coordinate to drawing
          onDrawingClick({
            longitude: e.lngLat.lng,
            latitude: e.lngLat.lat,
          });
        } else if (isEditMode && editingAreaId && onCoordinateAdd) {
          // In edit mode, add coordinate to the editing area
          onCoordinateAdd(editingAreaId, {
            lat: e.lngLat.lat,
            lon: e.lngLat.lng,
          });
        }
      }
    };

    const waitForMapLoad = () => {
      if (map.current.isStyleLoaded()) {
        map.current.off('click', handleMapClick); // Remove existing listener
        map.current.on('click', handleMapClick);

        // Also add area click handler specifically for the fill layer
        if (onAreaClick) {
          map.current.off('click', 'areas-fill'); // Remove existing listener
          map.current.on('click', 'areas-fill', (e: any) => {
            const feature = e.features[0];
            if (feature && feature.properties) {
              const areaId = feature.properties.id;
              const area = areas.find((a) => a.id === areaId);
              if (area) {
                onAreaClick(area);
              }
            }
          });
        }
      } else {
        setTimeout(waitForMapLoad, 100);
      }
    };

    waitForMapLoad();

    return () => {
      if (map.current) {
        map.current.off('click', handleMapClick);
        map.current.off('click', 'areas-fill');
      }
    };
  }, [
    onAreaClick,
    areas,
    isEditMode,
    editingAreaId,
    onCoordinateAdd,
    drawingEnabled,
    onDrawingClick,
  ]);

  // Update areas when areas array changes
  useEffect(() => {
    if (!map.current) return;

    // Wait for map to be fully loaded
    const updateAreas = () => {
      if (!map.current.isStyleLoaded()) {
        setTimeout(updateAreas, 100);
        return;
      }

      // Check if source exists
      const source = map.current.getSource('areas');
      if (!source) {
        setTimeout(updateAreas, 100);
        return;
      }

      const features = areas
        .map((area: Area) => {
          // Validate coordinates
          if (!area.coords || area.coords.length < 3) {
            return null;
          }

          // Ensure polygon is closed (first and last coordinates should be the same)
          const coordinates = area.coords.map((coord) => [
            coord.lon,
            coord.lat,
          ]);

          // Validate coordinate values
          const validCoords = coordinates.filter((coord) => {
            const [lon, lat] = coord;
            return (
              !isNaN(lon) &&
              !isNaN(lat) &&
              lon >= -180 &&
              lon <= 180 &&
              lat >= -90 &&
              lat <= 90
            );
          });

          if (validCoords.length !== coordinates.length) {
            // Some coordinates were invalid
          }

          if (validCoords.length < 3) {
            return null;
          }

          // Close the polygon if needed
          const firstCoord = validCoords[0];
          const lastCoord = validCoords[validCoords.length - 1];
          if (
            firstCoord[0] !== lastCoord[0] ||
            firstCoord[1] !== lastCoord[1]
          ) {
            validCoords.push(firstCoord);
          }

          return {
            type: 'Feature',
            id: area.id,
            properties: {
              id: area.id,
              name: area.name,
              description: area.description,
              colorCode: getAreaColor(area),
            },
            geometry: {
              type: 'Polygon',
              coordinates: [validCoords],
            },
          };
        })
        .filter(Boolean); // Remove null features

      const geojsonData = {
        type: 'FeatureCollection',
        features,
      };

      try {
        source.setData(geojsonData);
        // Trigger coordinate marker refresh after areas are updated
        setTimeout(() => {
          setCoordinateUpdateTrigger((prev) => prev + 1);
        }, 50);
      } catch (error) {
        console.error('Error setting area data:', error);
      }
    };

    updateAreas();
  }, [areas]);

  // Handle selected area highlighting
  useEffect(() => {
    if (!map.current || !map.current.isStyleLoaded()) return;

    // Reset all area states
    areas.forEach((area) => {
      map.current.setFeatureState(
        { source: 'areas', id: area.id },
        { selected: false }
      );
    });

    // Set selected area state
    if (selectedAreaId) {
      map.current.setFeatureState(
        { source: 'areas', id: selectedAreaId },
        { selected: true }
      );
    }
  }, [selectedAreaId, areas]);

  // Handle coordinate editing markers
  useEffect(() => {
    if (!map.current || !map.current.isStyleLoaded()) {
      return;
    }

    // Get editing area coordinates and color
    const editingArea =
      isEditMode && editingAreaId
        ? areas.find((a) => a.id === editingAreaId)
        : null;
    const editingAreaCoords = editingArea?.coords || null;
    const editingAreaColor = editingArea
      ? getAreaColor(editingArea)
      : 'var(--fi-accent, #FF6A00)';

    // Clear existing coordinate markers
    coordinateMarkers.current.forEach((marker: any) => {
      marker.remove();
    });
    coordinateMarkers.current = [];

    if (
      isEditMode &&
      editingAreaId &&
      editingAreaCoords &&
      editingAreaCoords.length > 0
    ) {
      // Create markers for each coordinate
      editingAreaCoords.forEach((coord, index) => {
        const markerElement = document.createElement('div');
        markerElement.style.width = '14px';
        markerElement.style.height = '14px';
        markerElement.style.borderRadius = '50%';
        markerElement.style.backgroundColor = editingAreaColor;
        markerElement.style.border = '2px solid white';
        markerElement.style.cursor = 'grab';
        markerElement.style.boxShadow = '0 2px 4px rgba(0,0,0,0.3)';
        markerElement.style.fontSize = '8px';
        markerElement.style.fontWeight = 'bold';
        markerElement.style.color = 'white';
        markerElement.style.display = 'flex';
        markerElement.style.alignItems = 'center';
        markerElement.style.justifyContent = 'center';
        markerElement.style.userSelect = 'none';
        markerElement.textContent = (index + 1).toString();
        markerElement.title = `Point ${index + 1}: (${coord.lat.toFixed(
          4
        )}, ${coord.lon.toFixed(4)})`;

        // Right-click to delete
        markerElement.addEventListener('contextmenu', (e) => {
          e.preventDefault();
          e.stopPropagation();

          if (
            onCoordinateDelete &&
            editingAreaCoords &&
            editingAreaCoords.length > 3
          ) {
            const shouldDelete = window.confirm(
              `Delete coordinate ${index + 1}?`
            );
            if (shouldDelete) {
              onCoordinateDelete(editingAreaId, index);
            }
          } else {
            alert(
              'Cannot delete coordinate. Area must have at least 3 points.'
            );
          }
        });

        // Hover effects - darken the color on hover
        markerElement.addEventListener('mouseenter', () => {
          // Create a darker version of the color
          const darkerColor =
            editingAreaColor === '#D0D2D8'
              ? '#B0B2B8'
              : editingAreaColor === '#5F27CD'
              ? '#4A1F9A'
              : editingAreaColor; // Keep original if not a standard color
          markerElement.style.backgroundColor = darkerColor;
          markerElement.style.width = '16px';
          markerElement.style.height = '16px';
          markerElement.style.cursor = 'grab';
        });

        markerElement.addEventListener('mouseleave', () => {
          markerElement.style.backgroundColor = editingAreaColor;
          markerElement.style.width = '14px';
          markerElement.style.height = '14px';
          markerElement.style.cursor = 'grab';
        });

        // Create and position the marker
        const marker = new (mapboxgl as any).Marker({
          element: markerElement,
          draggable: true,
        })
          .setLngLat([coord.lon, coord.lat])
          .addTo(map.current);

        // Handle dragging
        marker.on('dragstart', () => {
          markerElement.style.opacity = '0.7';
          markerElement.style.cursor = 'grabbing';
        });

        marker.on('dragend', () => {
          markerElement.style.opacity = '1';
          markerElement.style.cursor = 'grab';
          const lngLat = marker.getLngLat();
          if (onCoordinateMove) {
            onCoordinateMove(editingAreaId, index, {
              lat: lngLat.lat,
              lon: lngLat.lng,
            });
          }
        });

        coordinateMarkers.current.push(marker);
      });
    }

    return () => {
      coordinateMarkers.current.forEach((marker: any) => {
        marker.remove();
      });
      coordinateMarkers.current = [];
    };
  }, [
    isEditMode,
    editingAreaId,
    coordinateUpdateTrigger,
    onCoordinateDelete,
    onCoordinateMove,
  ]);

  // Handle cursor styling for drawing mode
  useEffect(() => {
    if (!map.current) return;

    const canvas = map.current.getCanvas();
    if (drawingEnabled) {
      canvas.style.cursor = 'crosshair';
    } else {
      canvas.style.cursor = '';
    }

    return () => {
      if (canvas) {
        canvas.style.cursor = '';
      }
    };
  }, [drawingEnabled]);

  // Handle drawing coordinates display
  useEffect(() => {
    const updateDrawingMarkers = () => {
      if (!map.current || !map.current.isStyleLoaded()) {
        setTimeout(updateDrawingMarkers, 100);
        return;
      }

      // Clear existing drawing markers
      drawingMarkers.current.forEach((marker: any) => {
        marker.remove();
      });
      drawingMarkers.current = [];

      if (drawingEnabled && drawingCoords && drawingCoords.length > 0) {
        // Create markers for each drawing coordinate
        drawingCoords.forEach((coord, index) => {
          const markerElement = document.createElement('div');
          markerElement.style.width = '12px';
          markerElement.style.height = '12px';
          markerElement.style.borderRadius = '50%';
          markerElement.style.backgroundColor = '#5F27CD'; // Use default purple for drawing
          markerElement.style.border = '2px solid white';
          markerElement.style.boxShadow = '0 2px 4px rgba(0,0,0,0.3)';
          markerElement.style.fontSize = '8px';
          markerElement.style.fontWeight = 'bold';
          markerElement.style.color = 'white';
          markerElement.style.display = 'flex';
          markerElement.style.alignItems = 'center';
          markerElement.style.justifyContent = 'center';
          markerElement.style.userSelect = 'none';
          markerElement.textContent = (index + 1).toString();
          markerElement.title = `Drawing Point ${
            index + 1
          }: (${coord.lat.toFixed(4)}, ${coord.lon.toFixed(4)})`;

          // Create and position the marker
          const marker = new (mapboxgl as any).Marker({
            element: markerElement,
            draggable: false,
          })
            .setLngLat([coord.lon, coord.lat])
            .addTo(map.current);

          drawingMarkers.current.push(marker);
        });

        // Draw lines between points if we have more than one
        if (drawingCoords.length > 1) {
          const lineCoordinates = drawingCoords.map((coord) => [
            coord.lon,
            coord.lat,
          ]);

          // Add or update drawing line source
          const drawingSource = map.current.getSource('drawing-line');
          if (drawingSource) {
            drawingSource.setData({
              type: 'Feature',
              geometry: {
                type: 'LineString',
                coordinates: lineCoordinates,
              },
            });
          } else {
            map.current.addSource('drawing-line', {
              type: 'geojson',
              data: {
                type: 'Feature',
                geometry: {
                  type: 'LineString',
                  coordinates: lineCoordinates,
                },
              },
            });

            map.current.addLayer({
              id: 'drawing-line-layer',
              type: 'line',
              source: 'drawing-line',
              paint: {
                'line-color': '#5F27CD', // Use default purple for drawing
                'line-width': 2,
                'line-dasharray': [4, 4],
              },
            });
          }
        } else {
          // Remove drawing line if not in drawing mode
          if (map.current.getLayer('drawing-line-layer')) {
            map.current.removeLayer('drawing-line-layer');
          }
          if (map.current.getSource('drawing-line')) {
            map.current.removeSource('drawing-line');
          }
        }
      } else {
        // Remove drawing line if not in drawing mode
        if (map.current.getLayer('drawing-line-layer')) {
          map.current.removeLayer('drawing-line-layer');
        }
        if (map.current.getSource('drawing-line')) {
          map.current.removeSource('drawing-line');
        }
      }
    };

    updateDrawingMarkers();

    return () => {
      drawingMarkers.current.forEach((marker: any) => {
        marker.remove();
      });
      drawingMarkers.current = [];
    };
  }, [drawingEnabled, drawingCoords]);

  // Handle user location with GeoJSON approach
  useEffect(() => {
    if (!map.current) {
      return;
    }

    const updateUserLocation = () => {
      // Always use the current userLocation value from the ref
      const currentUserLocation = userLocationRef.current;

      if (!map.current.isStyleLoaded()) {
        setTimeout(updateUserLocation, 100);
        return;
      }

      // Check if source exists
      const source = map.current.getSource('user-location');
      if (!source) {
        setTimeout(updateUserLocation, 100);
        return;
      }

      let features: any[] = [];

      if (currentUserLocation) {
        const { latitude, longitude } = currentUserLocation;

        // Validate coordinates
        if (
          typeof latitude !== 'number' ||
          typeof longitude !== 'number' ||
          isNaN(latitude) ||
          isNaN(longitude) ||
          latitude < -90 ||
          latitude > 90 ||
          longitude < -180 ||
          longitude > 180
        ) {
          return;
        }

        features = [
          {
            type: 'Feature' as const,
            id: 'user-location',
            properties: {
              type: 'user-location',
            },
            geometry: {
              type: 'Point' as const,
              coordinates: [longitude, latitude], // GeoJSON format: [longitude, latitude]
            },
          },
        ];
      }

      const geojsonData = {
        type: 'FeatureCollection' as const,
        features,
      };

      try {
        source.setData(geojsonData);
      } catch (error) {
        // Retry after a short delay if there was an error
        setTimeout(updateUserLocation, 200);
      }
    };

    updateUserLocation();
  }, [userLocation]);

  return <MapContainer ref={mapContainer} />;
};

export default InteractiveMapView;
